import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faShieldAlt,
  faExclamationTriangle,
  faChevronDown

} from "@fortawesome/free-solid-svg-icons";

import LogoImage from "../../utils/images/logo_21.png";
import PlaymarketBadge from "../../utils/images/playmarket.png";
import AppStoreBadge from "../../utils/images/appstore.png";
import Image from "../../utils/images/mega1.jpg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { verifyOtp, clearOtpError } from "../../store/slices/Auth/OtpSlice";
import { resendOtp, clearResendOtpError, resetResendOtp } from "../../store/slices/Auth/ResendOtpSlice";
import { AppDispatch, RootState } from "../../store/store";
import TechnicalSupport from "../../components/Techniqalsupport";

export default function OTPVerification(): React.JSX.Element {
  const [timeLeft, setTimeLeft] = useState<number>(180); // 3 minutes in seconds
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [otpValue, setOtpValue] = useState<string>("");
  const [canResend, setCanResend] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber || "";
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'az';
  });
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const translations = {
    az: {
      personalCabinet: "Şəxsi kabinet",
      back: "Geri",
      otpDescription: "Şəxsi kabinetə giriş etmək üçün {phone} nömrəsinə göndərilmiş birdəfəlik SMS kodu daxil edin.",
      otpCode: "OTP kod",
      otpPlaceholder: "××××",
      confirm: "Təsdiq et",
      loading: "Yüklənir...",
      timeRemaining: "SMS kod üçün qalan vaxt",
      timeExpired: "Vaxt Bitdi",
      timeExpiredMessage: "Təsdiqləmə kodu üçün müddət bitdi. Zəhmət olmasa, yeni kod istəyin.",
      resendCode: "Yeni kod göndər",
      sending: "Göndərilir...",
      close: "Bağla",
      technicalSupport: "Texniki dəstək",
    },
    ru: {
      personalCabinet: "Личный кабинет",
      back: "Назад",
      otpDescription: "Для входа в личный кабинет введите одноразовый SMS-код, отправленный на номер {phone}.",
      otpCode: "OTP код",
      otpPlaceholder: "××××",
      confirm: "Подтвердить",
      loading: "Загрузка...",
      timeRemaining: "Оставшееся время для SMS-кода",
      timeExpired: "Время Истекло",
      timeExpiredMessage: "Время для кода подтверждения истекло. Пожалуйста, запросите новый код.",
      resendCode: "Отправить новый код",
      sending: "Отправка...",
      close: "Закрыть",
      technicalSupport: "Техническая поддержка",
    },
    en: {
      personalCabinet: "Personal Cabinet",
      back: "Back",
      otpDescription: "To log in to your personal cabinet, enter the one-time SMS code sent to {phone}.",
      otpCode: "OTP code",
      otpPlaceholder: "××××",
      confirm: "Confirm",
      loading: "Loading...",
      timeRemaining: "Time remaining for SMS code",
      timeExpired: "Time Expired",
      timeExpiredMessage: "The verification code has expired. Please request a new code.",
      resendCode: "Send new code",
      sending: "Sending...",
      close: "Close",
      technicalSupport: "Technical Support",
    }
  };
  const t = translations[currentLanguage as keyof typeof translations];

  const languages = [
    { code: 'az', label: 'AZ', name: 'Azərbaycan' },
    { code: 'ru', label: 'RU', name: 'Русский' },
    { code: 'en', label: 'EN', name: 'English' }
  ];



  const { loading, error, isOtpAuthenticated } = useSelector(
    (state: RootState) => state.authOtp
  );

  const {
    loading: resendLoading,
    success: resendSuccess,
    error: resendError,
    message: resendMessage
  } = useSelector((state: RootState) => state.resendOtp);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    dispatch(clearOtpError());
    dispatch(clearResendOtpError());

    return () => {
      dispatch(clearOtpError());
      dispatch(clearResendOtpError());
      dispatch(resetResendOtp());
    };
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (timeLeft > 0 && !isExpired) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isExpired) {
      setIsExpired(true);
      setCanResend(true);
      setShowAlert(true);
    }
  }, [timeLeft, isExpired]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCooldown === 0 && !isExpired) {
      setCanResend(true);
    }
  }, [resendCooldown, isExpired]);

  useEffect(() => {
    if (resendSuccess) {
      setTimeLeft(180);
      setIsExpired(false);
      setCanResend(false);
      setResendCooldown(180);
      setOtpValue("");
      setShowAlert(false);

      setTimeout(() => {
        dispatch(resetResendOtp());
      }, 3000);
    }
  }, [resendSuccess, dispatch]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const closeAlert = (): void => {
    setShowAlert(false);
    navigate(('/login'))

  };

  const handleLanguageChange = (languageCode: string) => {
    localStorage.setItem('selectedLanguage', languageCode);
    setCurrentLanguage(languageCode);
    setIsLanguageDropdownOpen(false);
  };

  useEffect(() => {
    if (isOtpAuthenticated) {
      navigate("/");
    }
  }, [isOtpAuthenticated, navigate]);

  const handleSingleOtpChange = (value: string): void => {
    if (isExpired) return;

    const cleanValue = value.replace(/\D/g, "").slice(0, 4);
    setOtpValue(cleanValue);

    if (error) {
      dispatch(clearOtpError());
    }
    if (resendError) {
      dispatch(clearResendOtpError());
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (isExpired) {
      setShowAlert(true);
      return;
    }

    if (otpValue.length === 4 && phoneNumber) {
      try {
        await dispatch(
          verifyOtp({
            phoneNumber: phoneNumber,
            otpCode: otpValue,
          })
        ).unwrap();
      } catch (err) {
        console.error("OTP verification failed:", err);
      }
    }
  };

  const handleResendOtp = async (): Promise<void> => {
    if (!canResend || resendLoading || resendCooldown > 0) return;

    try {
      await dispatch(
        resendOtp({
          phone_number: phoneNumber,
        })
      ).unwrap();
    } catch (err) {
      console.error("OTP resend failed:", err);
    }
  };

  const maskPhoneNumber = (phone: string): string => {
    if (phone.length > 7) {
      const start = phone.slice(0, 6);
      const end = phone.slice(-2);
      return `${start}******${end}`;
    }
    return phone;
  };

  return (
    <>
      <div
        className="min-h-screen flex "
        style={{ backgroundColor: "#523a48" }}
      >
        {/* Left side - OTP form */}
        <div
          className="w-full lg:flex-1 flex justify-center p-8 relative"
          style={{ backgroundColor: "#523a48" }}
        >
          <div className="w-full max-w-md relative z-10">
            {/* Logo */}
            <div className="text-center mb-6">
              <div className="flex items-center mb-10">
                <img
                  src={LogoImage}
                  alt="Meqa Sığorta Logo"
                  className="w-28 xs:w-32 sm:w-32 md:w-36 lg:w-40 mr-auto object-contain"
                />

                {/* Language Selector */}
                <div className="flex justify-end mb-1">
                  <div className="relative" ref={languageDropdownRef}>
                    <button
                      onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                      className="flex items-center gap-2 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                    >
                      <span className="text-sm font-medium uppercase">{currentLanguage}</span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`text-xs transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {/* Language Dropdown */}
                    {isLanguageDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg overflow-hidden z-50">
                        {languages
                          .filter(lang => lang.code !== currentLanguage)
                          .map((language) => (
                            <button
                              key={language.code}
                              onClick={() => handleLanguageChange(language.code)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors flex items-center justify-between group"
                            >
                                                            <span className="text-gray-700 text-sm font-medium">
                                                                {language.name}
                                                            </span>
                              <span className="text-xs text-gray-500 font-semibold uppercase group-hover:text-gray-700">
                                                                {language.label}
                                                            </span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>


            <div className="mb-4 inline-flex items-center px-3 py-1 rounded-md text-xs bg-white/10 border border-white/20">
              <Link
                to="/login"
                className="flex items-center text-white hover:text-pink-200 transition-colors cursor-pointer"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                <span className="text-sm font-medium">{t.back}</span>
              </Link>
            </div>
            <p className="text-pink-100 text-md text-left mb-4 font-bold">
              {t.otpDescription.replace('{phone}', maskPhoneNumber(phoneNumber))}

            </p>

            {/* Error Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-300 rounded-lg">
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}

            {/* Resend Error */}
            {resendError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-300 rounded-lg">
                <p className="text-red-200 text-sm">{resendError}</p>
              </div>
            )}

            {/* Success Message */}
            {resendSuccess && resendMessage && (
              <div className="mb-4 p-3 bg-green-500/20 border border-green-300 rounded-lg">
                <p className="text-green-200 text-sm">{resendMessage}</p>
              </div>
            )}


            <form>
              <div className="space-y-6">

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t.otpCode}

                  </label>
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faShieldAlt}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="tel"
                      value={otpValue}
                      onChange={(e) => handleSingleOtpChange(e.target.value)}
                      placeholder="××××"
                      maxLength={4}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent font-mono tracking-widest ${
                        isExpired ? "cursor-not-allowed opacity-50" : ""
                      }`}
                      disabled={isExpired}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6  pt-6 ">
                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isExpired || otpValue.length < 4 || loading}
                  className={`w-full py-3 px-4 rounded-lg flex items-center justify-center shadow-lg transition duration-200 ${
                    isExpired || otpValue.length < 4 || loading
                      ? "bg-colorpink text-gray-200 cursor-not-allowed opacity-50"
                      : "hover:shadow-xl hover:opacity-90"
                  }`}
                  style={{
                    backgroundColor:
                      isExpired || otpValue.length < 4 || loading
                        ? undefined
                        : "#a80067",
                  }}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span className="text-white">{t.loading}</span>
                    </>
                  ) : (
                    <span className="text-white font-semibold">{t.confirm}</span>
                  )}
                </button>

                {/* Resend OTP Button */}


                <div className="flex justify-start gap-2 ">
                  <div>
                                        <span
                                          className={`text-sm font-medium ${
                                            timeLeft <= 30 ? "text-red-300" : "text-white"
                                          }`}
                                        >
                                            {t.timeRemaining}
                                        </span>
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-md text-xs ${
                      timeLeft <= 30
                        ? "bg-red-500/20 border border-red-300"
                        : "bg-white/10 border border-white/20"
                    }`}
                  >
                                        <span
                                          className={`font-mono font-bold ${
                                            timeLeft <= 30 ? "text-red-300" : "text-white"
                                          }`}
                                        >
                                            {formatTime(timeLeft)}
                                        </span>
                  </div>
                </div>

                {/* App Store and Play Store Badges */}
                <div className="mt-6 pt-5 border-t border-white/20">
                  <div className="flex justify-center items-center gap-4">
                    <a
                      href="https://play.google.com/store/apps/details?id=com.megasigorta.app&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={PlaymarketBadge}
                        alt="Google Play-də əldə edin"
                        className="h-12 w-36 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </a>

                    <a
                      href="https://apps.apple.com/az/app/meqa-s%C4%B1%C4%9Forta/id6751800309"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={AppStoreBadge}
                        alt="App Store-dan yükləyin"
                        className="h-12 w-36 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </a>
                  </div>

                  {/* Technical Support Link */}
                  <div className="mt-4 text-center">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsSupportModalOpen(true);
                      }}
                      className="text-pink-100 hover:text-white text-sm hover:underline transition-colors underline-offset-2 cursor-pointer inline-flex items-center gap-2"
                    >
                      {t.technicalSupport}
                    </a>
                  </div>
                </div>
              </div>
            </form>

          </div>

        </div>

        <div className="flex-1 relative overflow-hidden hidden lg:block">
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: ` url(${Image})`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "local",
            }}
          />
        </div>

        {/* Alert Modal */}
        {showAlert && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
              <div className="text-center mb-4">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="text-red-500 text-2xl"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {t.timeExpired}

                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {t.timeExpiredMessage}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleResendOtp}
                  disabled={resendLoading}
                  className="w-full bg-pink-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 hover:bg-pink-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  style={{ backgroundColor: resendLoading ? undefined : "#a80067" }}
                >
                  {resendLoading ? t.sending : t.resendCode}

                </button>
                <button
                  onClick={closeAlert}
                  className="w-full bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg transition duration-200 hover:bg-gray-400"
                >
                  {t.close}

                </button>
              </div>
            </div>
          </div>
        )}

        <TechnicalSupport
          isOpen={isSupportModalOpen}
          onClose={() => setIsSupportModalOpen(false)}
          currentLanguage={currentLanguage}
        />
      </div>
    </>
  );
}